import { FC, memo, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AnyAction } from "redux";
import { SHARED_REDUCER_ACTIONS } from "store/reducers/shared.reducer";
import { ROLES } from "utils/helpers/enums/roles.enums";
import { domains } from "utils/helpers/enums/shared.enums";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";
import useUtils from "./useUtils";
import { OrganizationDTO } from "utils/helpers/models/organization/organization.dto";
import { localOrganizationsData } from "utils/constants/data";
import { successToaster } from "utils/helpers/common/alert-service";

const useStore = () => {
  const dispatch = useDispatch();
  const { setCSSVariable } = useUtils();

  const dispatchAction = useCallback((type: string, payload?: any): void => {
    const dispatcher: AnyAction = { type, payload };
    dispatch(dispatcher);
  }, [dispatch]);

  const setSiteZoomLevel = useCallback((level?: number) => {
    if (level) {
      const remZoom = 100 - level + 100 + ((100 - level) / 10) * 2.5;
      setCSSVariable("--zoom-level", level + "%");
      setCSSVariable("--zoom-view-height", `${remZoom}vh`);
      setCSSVariable("--zoom-view-width", `${remZoom}vw`);
      localStorage.setItem("zoomLevel", `${level}%`);
    } else {
      const zoomLevel = localStorage.getItem("zoomLevel") ?? "80%";
      const level = +(zoomLevel as string)?.replace("%", "");
      const remZoom = 100 - level + 100 + ((100 - level) / 10) * 2.5;
      if (zoomLevel) {
        setCSSVariable("--zoom-view-height", `${remZoom}vh`);
        setCSSVariable("--zoom-view-width", `${remZoom}vw`);
        setCSSVariable("--zoom-level", zoomLevel);
      }
    }
  }, [setCSSVariable]);

  const setPriorLogoutRoute = useCallback((route: string) => {
    localStorage.setItem("priorLogoutRoute", route);
  }, []);

  const getPriorLogoutRoute = useCallback((): string | null => {
    return localStorage.getItem("priorLogoutRoute");
  }, []);

  const removePriorLogoutRoute = useCallback((): void => {
    return localStorage.removeItem("priorLogoutRoute");
  }, []);

  const setLoading = useCallback((isLoading: boolean): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.LOADING, isLoading);
  }, [dispatchAction]);

  const setToken = useCallback((
    payload: null | string,
    isLoginAs: boolean = false,
  ): void => {
    if (isLoginAs) {
      dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_LOGIN_AS_TOKEN, payload });
      if (payload) localStorage.setItem("loginAsToken", payload);
    } else {
      dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_TOKEN, payload });
      if (payload) localStorage.setItem("token", payload);
    }
  }, [dispatch]);

  const setFiltersForLoginAsUser = useCallback((
    payload: null | string,
    isLoginAs: boolean = false,
  ): void => {
    if (isLoginAs) {
      dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_LOGIN_AS_TOKEN, payload });
      if (payload) localStorage.setItem("loginAsToken", payload);
    } else {
      dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_TOKEN, payload });
      if (payload) localStorage.setItem("token", payload);
    }
  }, [dispatch]);

  const setDeclaredResult = useCallback((payload: any): void => {
    dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_DECLARED_RESULT, payload });
  }, [dispatch]);

  const getPermissionByRoute = useCallback((route: string): string => {
    let permission: string = "";
    const routes: any = siteRoutes;
    const permissions: any = sitePermissions;

    for (let key in routes) {
      if (routes[key] === route.split("?")[0]) {
        permission = permissions[key];
        break;
      }
    }

    // Course Repeat Policy uses same permission as Course Repeat Fee so roles need not have a separate permission
    if (permission === sitePermissions.academicsSettingsCourseRepeatPolicy) {
      return sitePermissions.academicsSettingsCourseRepeatFee;
    }

    // Marks management should use the same permission as session management listing
    // if (permission === sitePermissions.academicMarksManagement) {
    //   return sitePermissions.academicSessionManagementListing;
    // }

    // Handle dynamic SIR routes
    if (route.includes('/sir/')) {
      return "";
    }

    return permission;
  }, []);

  const getRouteByPermission = useCallback((permission: string): string => {
    if (!permission) return "";
    let route: string = "";
    const routes: any = siteRoutes;
    const permissions: any = sitePermissions;
    for (let key in permissions) {
      if (permissions[key] === permission.trim()) {
        route = routes[key];
        break;
      }
    }

    return route;
  }, []);

  const setUser = useCallback((payload: any, isLoginAs: boolean = false): void => {
    if (isLoginAs) {
      localStorage.setItem("loginAsUser", JSON.stringify(payload));
      dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_LOGIN_AS_USER, payload);
    } else {
      dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_USER, payload);
      localStorage.setItem("user", JSON.stringify(payload));
    }
  }, [dispatchAction]);

  const getFilePathWithBackend = useCallback((path: string): string => {
    return `${process.env.REACT_APP_BASE_URL}/${path}`;
  }, []);

  const setPermissions = useCallback((permissions: any[], isLoginAs: boolean = false) => {
    if (!Array.isArray(permissions)) {
      console.error("setPermissions: Permissions must be an array", permissions);
      return;
    }
    const permissionsToSave = permissions.map((permission) => {
      return typeof permission === "string" ? permission : permission.name;
    });

    if (isLoginAs) {
      localStorage.setItem(
        "loginAsPermissions",
        JSON.stringify(permissionsToSave),
      );
      dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_LOGIN_AS_PERMISSIONS, [
        ...permissionsToSave,
      ]);
    } else {
      localStorage.setItem("permissions", JSON.stringify(permissionsToSave));
      dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_PERMISSIONS, [
        ...permissionsToSave,
      ]);
    }
  }, [dispatchAction]);

  const isLoggedInAs = useCallback((): boolean => {
    const loginAsToken = localStorage.getItem("loginAsToken");
    return loginAsToken ? true : false;
  }, []);

  const getPermissions = useCallback(() => {
    let permissions: any = [];
    if (isLoggedInAs()) {
      permissions = localStorage.getItem("loginAsPermissions");
    } else {
      permissions = localStorage.getItem("permissions");
    }
    if (permissions) {
      return JSON.parse(permissions);
    } else {
      return [];
    }
  }, [isLoggedInAs]);

  const getToken = useCallback((): string | null => {
    if (isLoggedInAs()) {
      return localStorage.getItem("loginAsToken");
    } else {
      return localStorage.getItem("token");
    }
  }, [isLoggedInAs]);

  const setRole = useCallback((role: any, isLoginAs: boolean = false): void => {
    if (isLoginAs) {
      localStorage.setItem("loginAsRole", JSON.stringify(role));
    } else {
      localStorage.setItem("role", JSON.stringify(role));
    }
  }, []);

  const hasAccess = useCallback((permission: string): boolean => {
    if (!permission) return true;


    const permissions = getPermissions();
    return permissions.includes(permission);
  }, [getPermissions]);

  const setUserAlreadyApplied = useCallback((applied: boolean) => {
    dispatchAction(SHARED_REDUCER_ACTIONS.HAS_USER_ALREADY_APPLIED, applied);
  }, [dispatchAction]);

  const doesFileSizeExceed = useCallback((file: File, size: number = 5) => {
    const sizeInKbs = size * 1024;
    return file.size / 1024 > sizeInKbs;
  }, []);

  const getRole = useCallback((): any => {
    let role = "";
    if (isLoggedInAs()) {
      role = localStorage.getItem("loginAsRole");
    } else {
      role = localStorage.getItem("role");
    }
    if (role) {
      return JSON.parse(role);
    } else {
      return null;
    }
  }, [isLoggedInAs]);

  const setLastLoggedInUserRole = useCallback((role: ROLES) => {
    localStorage.setItem("lastUserRole", role);
  }, []);

  const getLastLoggedInUserRole = useCallback(() => {
    return localStorage.getItem("lastUserRole");
  }, []);

  const getOrganization = useCallback((): OrganizationDTO | null => {
    const org = localStorage.getItem("organization");
    if (org) {
      return JSON.parse(org);
    } else {
      return null;
    }
  }, []);

  const setOrganizationGlobals = useCallback((organization: any) => {
    const { primary_color = null } = organization || {};
    if (organization?.fav_icon) {
      const icon: any = document.querySelector("link[rel*='icon']");
      if (icon) icon.href = organization?.fav_icon;
    }
    if (primary_color) {
      const hexToRGBA = (hex: string, opacity: number): string => {
        hex = hex.replace(/^#/, "");
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      };

      document.documentElement.style.setProperty("--primary", primary_color);
      document.documentElement.style.setProperty(
        "--primary-extralight",
        hexToRGBA(primary_color, 0.2),
      );
    }
  }, []);

  const setOrganization = useCallback((payload: any) => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_ORGANIZATION, payload);
    localStorage.setItem("organization", JSON.stringify(payload));
    setOrganizationGlobals(payload);
  }, [dispatchAction, setOrganizationGlobals]);

  const setDarkTheme = useCallback((isDarkTheme: boolean) => {
    const addThemeTransitionToEveryElement = () => {
      const elements = document.querySelectorAll("*");
      elements.forEach((element) => {
        element.classList.add("theme-transition");
      });
    };

    const removeThemeTransitionFromEveryElement = () => {
      const elements = document.querySelectorAll("*");
      elements.forEach((element) => {
        element.classList.remove("theme-transition");
      });
    };

    addThemeTransitionToEveryElement();
    if (isDarkTheme) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
    dispatchAction(SHARED_REDUCER_ACTIONS.THEME_CHANGE, isDarkTheme);

    setTimeout(() => {
      removeThemeTransitionFromEveryElement();
    }, 500);
  }, [dispatchAction]);

  const logout = useCallback(async () => {
    if (isLoggedInAs()) {
      dispatchAction(SHARED_REDUCER_ACTIONS.LOGIN_AS_LOGOUT);
      localStorage.removeItem("loginAsToken");
      localStorage.removeItem("loginAsUser");
      localStorage.removeItem("loginAsPermissions");
      localStorage.removeItem("loginAsRole");
    } else {
      const lastUserRole = getLastLoggedInUserRole();
      const organization = getOrganization();
      const lastRoute = window.location.pathname;
      localStorage.clear();
      setLastLoggedInUserRole(lastUserRole as ROLES);
      setOrganization(organization);
      localStorage.setItem("priorLogoutRoute", lastRoute);
      dispatchAction(SHARED_REDUCER_ACTIONS.LOGOUT);
      setDarkTheme(false);
      successToaster("Logout Successfully");
    }
  }, [isLoggedInAs, getLastLoggedInUserRole, getOrganization, setLastLoggedInUserRole, setOrganization, dispatchAction, setDarkTheme]);

  const getPriorLoginAsRoute = useCallback((): string | null => {
    return localStorage.getItem("priorLoginAsRoute");
  }, []);

  const removePriorLoginAsRoute = useCallback(() => {
    localStorage.removeItem("priorLoginAsRoute");
  }, []);

  const getDomainType = useCallback(() => {
    const domain = window.location.hostname;
    if (
      domain === "localhost" ||
      domain === "www.umsoncloud.com" ||
      domain === "umsoncloud.com"
    ) {
      return domains.mainDomain;
    } else {
      return domains.subDomain;
    }
  }, []);

  const getDomainDefaultUnAuthRoute = useCallback(() => {
    const domainType = getDomainType();
    const lastUserRole = getLastLoggedInUserRole();
    if (domainType == domains.mainDomain) {
      return siteRoutes.superAdminLogin;
    } else if (
      lastUserRole === ROLES.ADMIN ||
      lastUserRole === ROLES.EMPLOYEES
    ) {
      return siteRoutes.adminLogin;
    } else {
      return siteRoutes.ePortalLogin;
    }
  }, [getDomainType, getLastLoggedInUserRole]);

  const getUser = useCallback((): any => {
    let user = null;
    if (isLoggedInAs()) {
      user = localStorage.getItem("loginAsUser");
    } else {
      user = localStorage.getItem("user");
    }
    if (user && user !== "undefined") {
      return JSON.parse(user);
    } else {
      return null;
    }
  }, [isLoggedInAs]);

  const getDomainDefaultAuthRoute = useCallback(() => {
    const domainType = getDomainType();
    if (domainType == domains.mainDomain) {
      return siteRoutes.superAdminDashboard;
    } else {
      const userRole = getRole();
      if (
        userRole?.guard_name == ROLES.ADMIN ||
        userRole?.guard_name === ROLES.EMPLOYEES
      ) {
        if (hasAccess(sitePermissions.adminDashboard)) {
          return siteRoutes.adminDashboard;
        } else {
          const permissions = getPermissions();
          const route = getRouteByPermission(permissions[0]);
          return route;
        }
      } else if (userRole?.guard_name == ROLES.USER) {
        return siteRoutes.ePortalDashboard;
      } else if (userRole?.guard_name === "students") {
        return siteRoutes.studentDashboard;
      }
    }
  }, [getDomainType, getRole, hasAccess, getPermissions, getRouteByPermission]);

  const isSuperAdmin = useCallback(() => {
    const role = getRole();
    return role?.guard_name === ROLES.SUPER_ADMIN;
  }, [getRole]);

  const setPriorLoginAsRoute = useCallback((route: string) => {
    localStorage.setItem("priorLoginAsRoute", route);
  }, []);



  const getCurrentOrgEportalLink = useCallback((): string => {
    const organization = getOrganization();
    let link: string = "";
    const isProductionMode = () => {
      return (
        process.env?.REACT_APP_PRODUCTION_MODE &&
        !process.env?.REACT_APP_IS_STAGING
      );
    };

    if (organization?.cname && isProductionMode()) {
      const orgDomains = organization.cname.split(",");
      const eportalDomain = orgDomains.find((d) => d.includes("eportal"));
      link = `https://${eportalDomain}/e-portal/login`;
    } else {
      link = `http://${organization?.username}.umsoncloud.com/e-portal/login`;
    }

    return link;
  }, [getOrganization]);

  const handleSetOrganizationLocally = useCallback(() => {
    const domainType = getDomainType();
    if (domainType !== domains.mainDomain) {
      const { hostname } = window.location;
      const organization = localOrganizationsData.find((org) =>
        org.domains.includes(hostname),
      );
      if (organization) {
        setOrganizationGlobals(organization);
        setOrganization(organization);
      }
    }
  }, [getDomainType, setOrganizationGlobals, setOrganization]);

  const getRelativePathAfterLogin = useCallback((defaultRoute: string) => {
    const priorLogoutPath = localStorage.getItem("priorLogoutRoute");
    let routeToReturn = defaultRoute;
    if (priorLogoutPath) {
      const permission = getPermissionByRoute(priorLogoutPath);
      if (hasAccess(permission)) {
        routeToReturn = priorLogoutPath;
      }
      localStorage.removeItem("priorLogoutRoute");
    } else {
      const permission = getPermissionByRoute(defaultRoute);
      if (!hasAccess(permission)) {
        const permissions = getPermissions();
        if (permissions.length > 0) {
          const route = getRouteByPermission(permissions[0]);
          routeToReturn = route;
        }
      }
    }
    return routeToReturn;
  }, [getPermissionByRoute, hasAccess, getPermissions, getRouteByPermission]);

  const getSiteZoomLevel = useCallback(() => {
    const root = document.documentElement;
    const zoom = getComputedStyle(root)
      ?.getPropertyValue("--zoom-level")
      ?.trim();
    return +zoom.replace("%", "");
  }, []);

  const setUnreadNotificationsCount = useCallback((count: number) => {
    dispatchAction(
      SHARED_REDUCER_ACTIONS.SAVE_UNREAD_NOTIFICATIONS_COUNT,
      count,
    );
  }, [dispatchAction]);

  return {
    setUnreadNotificationsCount,
    setFiltersForLoginAsUser,
    setToken,
    setLoading,
    dispatchAction,
    setDarkTheme,
    logout,
    setUser,
    getLastLoggedInUserRole,
    getDomainDefaultUnAuthRoute,
    setPriorLoginAsRoute,
    getRelativePathAfterLogin,
    getDomainDefaultAuthRoute,
    setLastLoggedInUserRole,
    setSiteZoomLevel,
    getFilePathWithBackend,
    setPermissions,
    hasAccess,
    getDomainType,
    getSiteZoomLevel,
    getPermissions,
    removePriorLoginAsRoute,
    setOrganizationGlobals,
    setPriorLogoutRoute,
    removePriorLogoutRoute,
    getPriorLogoutRoute,
    getToken,
    doesFileSizeExceed,
    getPermissionByRoute,
    setOrganization,
    getOrganization,
    getRole,
    setUserAlreadyApplied,
    getRouteByPermission,
    getCurrentOrgEportalLink,
    setRole,
    getUser,
    isLoggedInAs,
    getPriorLoginAsRoute,
    isSuperAdmin,
    handleSetOrganizationLocally,
    setDeclaredResult,
  };
};

export default useStore;
